import { useState } from "react";

import { StaticImport } from "next/dist/shared/lib/get-img-props";
import NextImage, {
  ImageProps as NextImageProps,
  StaticImageData,
} from "next/image";

import placeholder from "assets/placeholder.jpeg";

const STRAPI_IMAGE_ROUTE = "https://storage.googleapis.com/whoppah-strapi";

const GOOGLE_IMAGE_ROUTES = [
  "https://storage.googleapis.com",
  "https://images.whoppah.com",
  "https://testing-images.whoppah.com",
];

const CDN_IMAGE_ROUTES_MAP = {
  source: "storage.googleapis.com/whoppah-production",
  target: "images.whoppah.com",
};

// `fill` images need a `sizes` hint, otherwise the browser assumes 100vw and fetches
// the largest srcset candidate. Most fill images in this app are cards or thumbnails
// laid out in a grid — roughly full width on mobile, half on tablet, a third on
// desktop — so that's the default. Full-width images (heroes, galleries) should pass
// sizes="100vw" themselves.
const DEFAULT_FILL_SIZES =
  "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw";

type ImageSrc = string | StaticImport | null;

const isStaticImageData = (src?: ImageSrc): src is StaticImageData => {
  type PossibleStaticImageData = StaticImageData | undefined;
  return typeof (src as PossibleStaticImageData)?.src === "string";
};

const isSVG = (src?: ImageSrc) => {
  if (typeof src === "string") {
    return src.endsWith(".svg");
  }

  if (isStaticImageData(src)) {
    return src.src.endsWith(".svg");
  }

  return false;
};

const isStrapiImage = (src?: ImageSrc) => {
  if (typeof src !== "string") {
    return false;
  }

  return src.startsWith(STRAPI_IMAGE_ROUTE);
};

const isGoogleStorageImage = (src?: ImageSrc) => {
  if (typeof src !== "string") {
    return false;
  }

  return GOOGLE_IMAGE_ROUTES.some(route => src.startsWith(route));
};

const isProductImage = (src?: ImageSrc) => {
  return isGoogleStorageImage(src) && !isStrapiImage(src);
};

const getCdnUrl = (src?: ImageSrc) => {
  if (typeof src !== "string") {
    return src;
  }

  return src.replace(CDN_IMAGE_ROUTES_MAP.source, CDN_IMAGE_ROUTES_MAP.target);
};

// ponytail: shim for the `next/legacy/image` props still used across ~64 callsites.
// Translates layout/objectFit/objectPosition → the modern fill/style API so we can
// drop next/legacy/image without touching every callsite. Migrate callsites off
// these props over time, then delete this block.
type LegacyLayout = "fill" | "responsive" | "intrinsic" | "fixed";
type LegacyImageProps = {
  layout?: LegacyLayout;
  objectFit?: React.CSSProperties["objectFit"];
  objectPosition?: React.CSSProperties["objectPosition"];
};

// `alt` is required in next/image but was optional under next/legacy/image; keep it
// optional here and default to "" so existing callsites don't need changes.
type ImageProps = Omit<NextImageProps, "src" | "alt"> &
  LegacyImageProps & {
    src?: ImageSrc;
    alt?: string;
    forceOptimize?: boolean;
    /** When the URL fails, hide the image (the parent shows its own fallback). */
    hideOnError?: boolean;
  };

const Image = ({
  src,
  forceOptimize,
  layout,
  objectFit,
  objectPosition,
  style,
  width,
  height,
  sizes,
  alt = "",
  hideOnError = false,
  onError,
  ...props
}: ImageProps) => {
  const [failed, setFailed] = useState(false);
  const fill = layout === "fill";

  const legacyStyle: React.CSSProperties | undefined =
    objectFit || objectPosition ? { objectFit, objectPosition } : undefined;

  // responsive/intrinsic scaled to the parent's width; fixed/fill keep intrinsic width/height.
  const layoutStyle: React.CSSProperties | undefined =
    layout === "responsive" || layout === "intrinsic"
      ? { width: "100%", height: "auto" }
      : undefined;

  const mergedStyle =
    legacyStyle || layoutStyle
      ? { ...legacyStyle, ...layoutStyle, ...style }
      : style;

  if (hideOnError && failed) {
    return null;
  }

  // `||`, not `??`: the CMS can return an empty string for an image that was
  // never set, and `<img src="">` makes the browser fetch the current document
  // (the whole HTML page) as the image. Empty must fall back like null does.
  const resolvedSrc = getCdnUrl(src) || placeholder;

  return (
    <NextImage
      unoptimized={!forceOptimize && (isSVG(src) || isProductImage(src))}
      src={resolvedSrc}
      alt={alt}
      placeholder="empty"
      // `fill` and width/height are mutually exclusive in next/image.
      {...(fill
        ? { fill: true, sizes: sizes ?? DEFAULT_FILL_SIZES }
        : { width, height, sizes })}
      style={mergedStyle}
      onError={event => {
        setFailed(true);
        onError?.(event);
      }}
      {...props}
    />
  );
};

export default Image;
